import { PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader } from 'antd';
import type { Key } from 'react';
import React from 'react';
import { DrawerWidth } from '../../config/dimensions';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { ActionOption, DrawerState, ModuleName } from '../../core/models/enum';
import { isUserAllowed } from '../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../helpers/NotificationHelper';
import { getNewRoute } from '../../helpers/RoutingHelper';
import BatchMatchOfficialsDrawer, {
    BatchOfficialsApplyMode,
    OfficialRolePair,
} from './BatchMatchOfficialsDrawer';
import { applyBatchOfficialsToMatches } from './batchMatchOfficialsApply';
import { Props } from './index';
import MatchForm from './match-form';
import MatchTable from './match-table';
import MatchSideDrawers, { MatchSideDrawerOpen } from './match-table/MatchSideDrawers';

interface State {
    drawerState: DrawerState;
    allowCreate: boolean;
    batchSelectMode: boolean;
    batchSelectedRowKeys: Key[];
    batchOfficialsDrawerVisible: boolean;
    matchSideDrawer: MatchSideDrawerOpen;
}

class Matches extends React.PureComponent<Props, State> {
    private tableRef: any;

    public constructor(props: Props) {
        super(props);

        const {
            location: { pathname },
        } = window;

        this.tableRef = React.createRef();

        this.state = {
            drawerState:
                pathname === Routes.ROUTE_MATCHES_CREATE ? DrawerState.Edit : DrawerState.Closed,
            allowCreate: isUserAllowed(props.userProfile, ModuleName.Matches, ActionOption.Create),
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
            matchSideDrawer: null,
        };
    }

    private handleDrawerMode = (drawerState: DrawerState, id?: string): void => {
        const { history } = this.props;

        this.setState({
            drawerState,
        });

        history.push(
            getNewRoute(
                Routes.ROUTE_MATCHES,
                Routes.ROUTE_MATCHES_READ,
                Routes.ROUTE_MATCHES_CREATE,
                drawerState,
                ReplaceStrings.MATCH_ID,
                id
            )
        );
    };

    private updateTable = (): void => {
        this.tableRef.current.getMatches();
    };

    private startBatchOfficials = (): void => {
        this.setState({
            batchSelectMode: true,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
    };

    private cancelBatchOfficials = (): void => {
        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
    };

    private openBatchOfficialsDrawer = (): void => {
        if (this.state.batchSelectedRowKeys.length === 0) {
            return;
        }
        this.setState({ batchOfficialsDrawerVisible: true });
    };

    private closeBatchOfficialsDrawer = (): void => {
        this.setState({ batchOfficialsDrawerVisible: false });
    };

    private applyBatchOfficials = async (
        pairs: OfficialRolePair[],
        mode: BatchOfficialsApplyMode
    ): Promise<void> => {
        const { batchSelectedRowKeys } = this.state;
        const matchIds = batchSelectedRowKeys.map((k) => Number(k));

        const { ok, failed } = await applyBatchOfficialsToMatches(matchIds, pairs, mode);

        if (failed === 0) {
            success(`Officials updated on ${ok} match(es).`);
        } else if (ok === 0) {
            errorWithUserMessage('Could not update officials for any match.');
        } else {
            success(`Officials updated on ${ok} match(es); ${failed} failed.`);
        }

        this.setState({
            batchSelectMode: false,
            batchSelectedRowKeys: [],
            batchOfficialsDrawerVisible: false,
        });
        this.updateTable();
    };

    private openMatchOfficialsDrawer = (matchId: number): void => {
        this.setState({ matchSideDrawer: { type: 'officials', matchId } });
    };

    private openMatchDetailsDrawer = (matchId: number): void => {
        this.setState({ matchSideDrawer: { type: 'details', matchId } });
    };

    private closeMatchSideDrawer = (): void => {
        this.setState({ matchSideDrawer: null });
    };

    public render(): React.ReactElement {
        const { userProfile } = this.props;
        const {
            drawerState,
            allowCreate,
            batchSelectMode,
            batchSelectedRowKeys,
            batchOfficialsDrawerVisible,
            matchSideDrawer,
        } = this.state;

        const showDrawer = !!drawerState;

        const allowBatchMatchUpdate = isUserAllowed(
            userProfile,
            ModuleName.Matches,
            ActionOption.Update
        );

        const headerExtra: React.ReactElement[] = [];

        if (batchSelectMode) {
            headerExtra.push(
                <Button key="batch-cancel" onClick={this.cancelBatchOfficials}>
                    Cancel batch
                </Button>
            );
            headerExtra.push(
                <Button
                    key="batch-next"
                    type="primary"
                    disabled={batchSelectedRowKeys.length === 0}
                    onClick={this.openBatchOfficialsDrawer}
                >
                    Next: choose officials
                </Button>
            );
        } else if (allowBatchMatchUpdate) {
            headerExtra.push(
                <Button
                    key="batch-start"
                    icon={<UserAddOutlined />}
                    onClick={this.startBatchOfficials}
                >
                    Batch add officials
                </Button>
            );
        }

        if (allowCreate) {
            headerExtra.push(
                <Button
                    key="add-match"
                    type="primary"
                    style={{
                        zIndex: 10,
                        float: 'right',
                    }}
                    onClick={(): void => this.handleDrawerMode(DrawerState.Edit)}
                >
                    <PlusOutlined className="button-icon-display" />
                    Add match
                </Button>
            );
        }

        return (
            <>
                <PageHeader
                    title="Matches"
                    extra={headerExtra.length ? headerExtra : undefined}
                    style={{ padding: '16px 0px' }}
                />

                {batchSelectMode && (
                    <div style={{ marginBottom: 12, color: '#595959' }}>
                        Step 1: tick the matches to update, then click &quot;Next: choose
                        officials&quot;.
                    </div>
                )}

                <MatchTable
                    userProfile={userProfile}
                    wrappedComponentRef={this.tableRef}
                    onMatchOfficialsDrawerOpen={this.openMatchOfficialsDrawer}
                    onMatchDetailsDrawerOpen={this.openMatchDetailsDrawer}
                    batchSelectionMode={batchSelectMode}
                    batchSelectedRowKeys={batchSelectedRowKeys}
                    onBatchSelectedRowKeysChange={(keys: Key[]): void =>
                        this.setState({ batchSelectedRowKeys: keys })
                    }
                />

                <MatchSideDrawers
                    drawer={matchSideDrawer}
                    onClose={this.closeMatchSideDrawer}
                    onSaved={this.updateTable}
                    userProfile={this.props.userProfile}
                />

                <Drawer
                    title="Batch add officials — step 2"
                    visible={batchOfficialsDrawerVisible}
                    onClose={this.closeBatchOfficialsDrawer}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    <BatchMatchOfficialsDrawer
                        visible={batchOfficialsDrawerVisible}
                        selectedMatchCount={batchSelectedRowKeys.length}
                        onClose={this.closeBatchOfficialsDrawer}
                        onApply={this.applyBatchOfficials}
                    />
                </Drawer>

                <Drawer
                    title="Add match"
                    visible={showDrawer}
                    onClose={(): void => this.handleDrawerMode(DrawerState.Closed)}
                    width={DrawerWidth}
                    destroyOnClose
                >
                    {!!drawerState && (
                        <MatchForm
                            key="create"
                            handleClose={this.handleDrawerMode}
                            refreshAfterSave={this.updateTable}
                        />
                    )}
                </Drawer>
            </>
        );
    }
}

export default Matches;
